import {
    faChevronDown,
    faChevronRight,
    faCompressAlt,
    faExpandAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Container, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { DropdownAtom } from "../../../components/atoms/atom";
import { FilterBar } from "../../../components/molecule/molecule";
import ProjectHeader from "../../../components/ProjectHeader";
import din276Service from "../../../store/services/din276Service";
import {
    useGetLVByIdQuery,
    useLazyGetLVPositionsQuery,
    useUpdatePositionCostGroupsMutation,
} from "../../../store/services/lvService";
import { selectProjectDetails } from "../../../store/slices/projectDetailsSlice";
import { formatQuantityToLocale, showToast } from "../../../utils/method";
import "./cost-group-assignment.scss";
import CostGroupDropdown from "./CostGroupDropdown";

const LVCostGroupAssignmentPage = () => {
    const { t } = useTranslation();
    const { projectId, lvId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [hierarchicalPositions, setHierarchicalPositions] = useState([]);
    const { data: lvData } = useGetLVByIdQuery(
        { projectId, lvId },
        { skip: !projectId || !lvId },
    );
    const [triggerGetLVPositions] = useLazyGetLVPositionsQuery();
    const [updatePositionCostGroups] = useUpdatePositionCostGroupsMutation();
    const [din276CostGroups, setDin276CostGroups] = useState([]);
    const [selectedPositions, setSelectedPositions] = useState(new Set());
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [bulkSelectedCostGroup, setBulkSelectedCostGroup] = useState(null);
    const [, setIsBulkOperationLoading] = useState(false);
    const [groupFilter, setGroupFilter] = React.useState("all");

    const projectDetails = useSelector(selectProjectDetails);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lvId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [positionsResponse, din276Response] = await Promise.all([
                triggerGetLVPositions({
                    projectId,
                    lvId,
                    costGroup: true,
                    hierarchy: true,
                }).unwrap(),
                din276Service.getDIN276CostGroups(),
            ]);
            if (positionsResponse?.positions) {
                setHierarchicalPositions(positionsResponse.positions);
            }
            if (din276Response?.items) {
                setDin276CostGroups(din276Response.items);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            showToast("Failed to load cost group assignment data", { type: "error" });
        } finally {
            setLoading(false);
        }
    };

    // New function to handle cost group assignment for a single position
    const assignCostGroupToPosition = async (positionId, costGroupId) => {
        try {
            // Create assignments data in the same format as bulk assignment
            const assignmentsData = [
                {
                    positionId,
                    costGroupId,
                },
            ];

            const response = await updatePositionCostGroups({
                projectId,
                lvId,
                body: assignmentsData,
            }).unwrap();

            if (response?.successful > 0) {
                showToast(
                    t("lv.cost-group-assignment.single-assignment-success"),
                    { type: "success" },
                );

                const positionsResponse = await triggerGetLVPositions({
                    projectId,
                    lvId,
                    costGroup: true,
                    hierarchy: true,
                }).unwrap();
                if (positionsResponse?.positions) {
                    setHierarchicalPositions(positionsResponse.positions);
                }
            }
        } catch (error) {
            console.error("Error assigning cost group to position:", error);
            showToast(t("lv.cost-group-assignment.assignment-error"), {
                type: "error",
            });
        }
    };

    // Function to handle bulk cost group assignment
    const handleBulkCostGroupAssignment = async (costGroupId) => {
        if (!costGroupId || selectedPositions.size === 0) return;

        try {
            // Get array of position IDs from the selectedPositions Set
            // Filter out group IDs - only apply cost group to actual positions
            const positionIds = Array.from(selectedPositions).filter((id) => {
                // Find the position in the hierarchical structure
                let isActualPosition = false;

                const findPosition = (elements) => {
                    for (const element of elements) {
                        if (element.id === id) {
                            // If this is not a group, mark it as an actual position
                            isActualPosition = !element.isGroup;
                            return true;
                        }

                        if (element.elements?.length > 0) {
                            if (findPosition(element.elements)) {
                                return true;
                            }
                        }
                    }
                    return false;
                };

                findPosition(hierarchicalPositions);
                return isActualPosition;
            });

            // Check if any selected positions already have a different cost group assigned
            const positionsWithExistingCostGroups = [];
            const findPositionsWithCostGroups = (elements) => {
                elements.forEach((element) => {
                    if (positionIds.includes(element.id) && !element.isGroup) {
                        if (element.din276CostGroupId && element.din276CostGroupId !== costGroupId) {
                            const existingCostGroup = din276CostGroups.find(
                                (cg) => cg.id === element.din276CostGroupId
                            );
                            positionsWithExistingCostGroups.push({
                                itemNumber: element.itemNumber,
                                shortText: element.shortText,
                                existingCostGroup: existingCostGroup ? `${existingCostGroup.code} - ${existingCostGroup.name}` : 'Unknown'
                            });
                        }
                    }

                    if (element.elements?.length > 0) {
                        findPositionsWithCostGroups(element.elements);
                    }
                });
            };

            findPositionsWithCostGroups(hierarchicalPositions);

            // Show confirmation dialog if there are positions with existing cost groups
            if (positionsWithExistingCostGroups.length > 0) {
                const newCostGroup = din276CostGroups.find((cg) => cg.id === costGroupId);
                const newCostGroupText = newCostGroup ? `${newCostGroup.code} - ${newCostGroup.name}` : 'Unknown';

                const confirmMessage = t("lv.cost-group-assignment.bulk-change-confirmation", {
                    count: positionsWithExistingCostGroups.length,
                    newCostGroup: newCostGroupText,
                    interpolation: { escapeValue: false }
                });

                const detailsList = positionsWithExistingCostGroups
                    .slice(0, 3) // Show max 3 examples
                    .map(pos => `• ${t("lv.position")} ${pos.itemNumber}: (${t("lv.cost-group-assignment.currently")}: ${pos.existingCostGroup})`)
                    .join('\n');

                const moreText = positionsWithExistingCostGroups.length > 3
                    ? `\n${t("lv.cost-group-assignment.and-more", { count: positionsWithExistingCostGroups.length - 3 })}`
                    : '';

                const fullMessage = `${confirmMessage}\n\n${detailsList}${moreText}`;

                const confirmed = window.confirm(fullMessage);
                if (!confirmed) {
                    return; // User cancelled the operation
                }
            }

            setIsBulkOperationLoading(true);

            // Prepare the request payload for our API
            const assignmentsData = positionIds.map((positionId) => ({
                positionId,
                costGroupId,
            }));

            const response = await updatePositionCostGroups({
                projectId,
                lvId,
                body: assignmentsData,
            }).unwrap();

            if (response?.successful > 0) {
                // Update the positions in the hierarchical structure based on API response
                const updatedPositions = [...hierarchicalPositions];

                // Update the positions in the hierarchical structure
                const updatePositionsRecursively = (elements) => {
                    return elements.map((element) => {
                        // Only update actual positions that are in the filtered positionIds array
                        if (positionIds.includes(element.id)) {
                            return {
                                ...element,
                                din276CostGroupId: costGroupId,
                                costGroup: din276CostGroups.find(
                                    (cg) => cg.id === costGroupId,
                                ),
                            };
                        }

                        if (element?.elements?.length > 0) {
                            return {
                                ...element,
                                elements: updatePositionsRecursively(
                                    element.elements,
                                ),
                            };
                        }

                        return element;
                    });
                };

                const updatedHierarchicalPositions =
                    updatePositionsRecursively(updatedPositions);
                setHierarchicalPositions(updatedHierarchicalPositions);

                // Show success message
                showToast(t("lv.cost-group-assignment.bulk-update-success"), {
                    type: "success",
                });

                // Clear selections after successful update
                setSelectedPositions(new Set());
                setBulkSelectedCostGroup(null);
            } else {
                throw new Error(response?.data?.message || "Unknown error");
            }
        } catch (error) {
            console.error("Error applying bulk cost group assignment:", error);
            showToast(t("lv.cost-group-assignment.bulk-update-error"), {
                type: "error",
            });
        } finally {
            setIsBulkOperationLoading(false);
        }
    };

    const FILTER_ITEMS = [
        {
            type: "backButton",
            key: "back",
            label: t("common.back"),
        },
    ];

    const onFilterAction = (index, key) => {
        switch (key) {
            case "back":
                navigate(`/projects/${projectId}/lv`);
                break;
            default:
                break;
        }
    };

    const toggleGroupExpansion = (groupId) => {
        setExpandedGroups((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    };

    const renderHierarchicalPositions = (positions, level = 0) => {
        if (!positions || positions.length === 0) {
            return (
                <tr>
                    <td colSpan="5" className="text-center">
                        {t("lv.no-positions-found")}
                    </td>
                </tr>
            );
        }

        return positions.map((position) => {
            const isGroup = position.isGroup;
            const isExpanded = expandedGroups.has(position.id);
            const isSelected = selectedPositions.has(position.id);

            return (
                <React.Fragment key={position.id}>
                    <tr
                        className={isGroup ? "position-group" : "position-item"}
                    >
                        <td>
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={isSelected}
                                    onChange={(e) =>
                                        handlePositionSelect(
                                            position.id,
                                            e.target.checked,
                                            position,
                                        )
                                    }
                                />
                            </div>
                        </td>
                        <td>
                            <div className={`d-flex align-items-center cga-indent-${Math.min(level, 10)}`}>
                                {isGroup && (
                                    <button
                                        className="btn btn-sm toggle-btn mr-2 bg-transparent border-0"
                                        onClick={() =>
                                            toggleGroupExpansion(position.id)
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={
                                                isExpanded
                                                    ? faChevronDown
                                                    : faChevronRight
                                            }
                                        />
                                    </button>
                                )}
                                <span>{`${isGroup ? t("lv.group") : t("lv.position")} ${position.itemNumber}`}</span>
                            </div>
                        </td>
                        <td>
                            {!isGroup ? (
                                <CostGroupDropdown
                                    costGroups={din276CostGroups}
                                    value={position.din276CostGroupId}
                                    onChange={(e) =>
                                        assignCostGroupToPosition(
                                            position.id,
                                            e.target.value,
                                        )
                                    }
                                    isSearchable={true}
                                    isClearable={true}
                                    size="small"
                                />
                            ) : (
                                (() => {
                                    // Recursively count all sub-groups and all positions (not groups) and assigned positions in this group and all subgroups
                                    const countGroupAndPositions = (
                                        elements,
                                    ) => {
                                        let groupCount = 0,
                                            positionCount = 0,
                                            assignedCount = 0;
                                        if (!elements)
                                            return {
                                                groupCount,
                                                positionCount,
                                                assignedCount,
                                            };
                                        for (const el of elements) {
                                            const elIsGroup =
                                                el.elementType ===
                                                "ServiceSpecificationGroupDto" ||
                                                el.elementTypeDiscriminator ===
                                                "ServiceSpecificationGroupDto";
                                            if (elIsGroup) {
                                                groupCount += 1;
                                                const sub =
                                                    countGroupAndPositions(
                                                        el.elements,
                                                    );
                                                groupCount += sub.groupCount;
                                                positionCount +=
                                                    sub.positionCount;
                                                assignedCount +=
                                                    sub.assignedCount;
                                            } else if (
                                                el.elementType ===
                                                "PositionDto" ||
                                                el.elementTypeDiscriminator ===
                                                "PositionDto"
                                            ) {
                                                positionCount += 1;
                                                if (el.din276CostGroupId)
                                                    assignedCount += 1;
                                            }
                                        }
                                        return {
                                            groupCount,
                                            positionCount,
                                            assignedCount,
                                        };
                                    };
                                    const {
                                        groupCount,
                                        positionCount,
                                        assignedCount,
                                    } = countGroupAndPositions(
                                        position.elements,
                                    );
                                    return (
                                        <span className="cga-group-stats">
                                            {groupCount > 0 && (
                                                <>
                                                    {groupCount} {t('lv.cost-group-assignment.sub-groups')},{" "}
                                                </>
                                            )}
                                            {assignedCount}/{positionCount} {t('lv.cost-group-assignment.assigned-positions')}
                                        </span>
                                    );
                                })()
                            )}
                        </td>
                        <td className={`cga-bg-level-${Math.min(level, 3)}`}>
                            {position.shortText}
                        </td>
                        <td className="text-right">
                            {!isGroup &&
                                `${formatQuantityToLocale(position.lvQuantity) || ""} ${position.unitTag || ""}`}
                        </td>
                    </tr>

                    {/* Render child elements if this is a group and it's expanded */}
                    {isGroup &&
                        isExpanded &&
                        position.elements?.length > 0 &&
                        renderHierarchicalPositions(
                            position.elements,
                            level + 1,
                        )}
                </React.Fragment>
            );
        });
    };

    // Update this helper function to count the total number of elements with proper optional chaining
    const getTotalElementCount = (elements) => {
        if (!elements?.length) return 0;

        let count = 0;
        const countElements = (items) => {
            if (!items?.length) return;

            items.forEach((item) => {
                count++; // Count this element (group or position)
                if (item?.elements?.length) {
                    countElements(item.elements); // Count children
                }
            });
        };
        countElements(elements);
        return count;
    };

    /**
     * Function to recursively collect all IDs of an element and its descendants
     * @param {Object} element - The element to collect IDs from
     * @returns {Array} Array of collected IDs
     */
    const collectAllChildIds = (element) => {
        const ids = [element.id];

        if (element.elements?.length > 0) {
            element.elements.forEach((childElement) => {
                ids.push(...collectAllChildIds(childElement));
            });
        }

        return ids;
    };

    /**
     * Handle selection of an item (position or group)
     * @param {string} id - ID of the item being selected
     * @param {boolean} isChecked - Whether the item is being selected or deselected
     * @param {Object} element - The element object being selected
     */
    const handlePositionSelect = (id, isChecked, element) => {
        setSelectedPositions((prev) => {
            const newSet = new Set(prev);

            if (isChecked) {
                // If this is a group, collect and add all child IDs
                if (element.isGroup) {
                    collectAllChildIds(element).forEach((id) => newSet.add(id));
                } else {
                    // Otherwise just add this position
                    newSet.add(id);
                }
            } else {
                // If this is a group, remove all child IDs
                if (element.isGroup) {
                    collectAllChildIds(element).forEach((id) =>
                        newSet.delete(id),
                    );
                } else {
                    // Otherwise just remove this position
                    newSet.delete(id);
                }
            }

            return newSet;
        });
    };

    // Replace the calculateCostGroupAssignmentProgress function with a memoized version
    const costGroupAssignmentProgress = React.useMemo(() => {
        let totalPositions = 0;
        let assignedPositions = 0;

        // Helper function to count positions and their cost group assignments recursively
        const countPositionsRecursively = (elements) => {
            elements.forEach((element) => {
                // Only count actual positions, not groups
                if (!element.isGroup) {
                    totalPositions++;
                    if (element.din276CostGroupId) {
                        assignedPositions++;
                    }
                }

                // Recursively check child elements
                if (element?.elements?.length > 0) {
                    countPositionsRecursively(element.elements);
                }
            });
        };

        // Start counting from the hierarchical positions
        countPositionsRecursively(hierarchicalPositions);

        // Calculate percentage (avoid division by zero)
        const percentage =
            totalPositions > 0 ? (assignedPositions / totalPositions) * 100 : 0;

        return {
            totalPositions,
            assignedPositions,
            percentage: Math.round(percentage),
        };
    }, [hierarchicalPositions]);

    const selectedPositionsCount = (() => {
        let count = 0;
        const countSelectedPositions = (elements) => {
            elements.forEach((element) => {
                if (
                    selectedPositions.has(element.id) &&
                    !element.isGroup &&
                    (!element.elements || element.elements.length === 0)
                ) {
                    count++;
                }

                // Still recursively check child elements
                if (element.elements?.length) {
                    countSelectedPositions(element.elements);
                }
            });
        };

        countSelectedPositions(hierarchicalPositions);
        return count;
    })();

    // Add this memoized function to pre-compute all group IDs
    const allGroupIds = React.useMemo(() => {
        const ids = new Set();

        const collectGroupIds = (elements) => {
            if (!elements || !elements.length) return;

            for (const element of elements) {
                if (element.isGroup) {
                    ids.add(element.id);
                    // Only recurse if there are child elements
                    if (element.elements?.length) {
                        collectGroupIds(element.elements);
                    }
                }
            }
        };

        collectGroupIds(hierarchicalPositions);
        return ids;
    }, [hierarchicalPositions]);

    const { isNachtraege, name, key } = lvData || {
        isNachtraege: false,
        name: "",
        key: "",
    };

    let lvName = "";
    if (!isNachtraege) {
        lvName = name || "";
    }
    lvName = [key, name].filter(Boolean).join(" - ") || "";

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center cga-loading-container">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    // When rendering hierarchical positions, filter groups based on the selected filter:
    const filterGroups = (positions) => {
        // Recursively filter groups and their children based on groupFilter
        const filterRecursive = (nodes) => {
            return nodes
                .map((node) => {
                    const isGroup =
                        node.elementType === "ServiceSpecificationGroupDto";
                    if (isGroup && Array.isArray(node.elements)) {
                        // Recursively filter children
                        const filteredChildren = filterRecursive(node.elements);
                        // Count assigned/unassigned positions in all descendants
                        const countGroupAndPositions = (elements) => {
                            let positionCount = 0,
                                assignedCount = 0;
                            if (!elements)
                                return { positionCount, assignedCount };
                            for (const el of elements) {
                                const elIsGroup =
                                    el.elementType ===
                                    "ServiceSpecificationGroupDto";
                                if (elIsGroup && Array.isArray(el.elements)) {
                                    const sub = countGroupAndPositions(
                                        el.elements,
                                    );
                                    positionCount += sub.positionCount;
                                    assignedCount += sub.assignedCount;
                                } else if (el.elementType === "PositionDto") {
                                    positionCount += 1;
                                    if (el.din276CostGroupId)
                                        assignedCount += 1;
                                }
                            }
                            return { positionCount, assignedCount };
                        };
                        const { positionCount, assignedCount } =
                            countGroupAndPositions(node.elements);

                        // For "assigned", show group if it or any child has assigned positions
                        // For "unassigned", show group if it or any child has unassigned positions
                        if (groupFilter === "assigned") {
                            if (assignedCount > 0) {
                                return { ...node, elements: filteredChildren };
                            }
                            return null;
                        }
                        if (groupFilter === "unassigned") {
                            if (positionCount > assignedCount) {
                                return { ...node, elements: filteredChildren };
                            }
                            return null;
                        }
                        // "all"
                        return { ...node, elements: filteredChildren };
                    } else if (node.elementType === "PositionDto") {
                        if (
                            groupFilter === "assigned" &&
                            node.din276CostGroupId
                        )
                            return node;
                        if (
                            groupFilter === "unassigned" &&
                            !node.din276CostGroupId
                        )
                            return node;
                        if (groupFilter === "all") return node;
                        return null;
                    }
                    // For other node types, skip
                    return null;
                })
                .filter(Boolean);
        };
        return filterRecursive(positions);
    };

    return (
        <div className="lv-cost-group-assignment-page">
            <ProjectHeader
                projectName={projectDetails.name}
                tabTitle={`${t("lv.cost-group-assignment.title")} | ${lvName || ""}`}
            />
            <div className="page-container">
                <FilterBar items={FILTER_ITEMS} onFilterAction={onFilterAction} />

                {/* Bulk Action Toolbar */}
                <div className="bulk-action-toolbar">
                    <div className="bulk-action-content">
                        <div className="selected-count-badge">
                            <span className="badge bg-primary">
                                {selectedPositionsCount}
                            </span>
                            <span className="selected-text">
                                {t("lv.cost-group-assignment.positions-selected")}
                            </span>
                        </div>

                        <div className="bulk-dropdown">
                            <CostGroupDropdown
                                costGroups={din276CostGroups}
                                value={bulkSelectedCostGroup}
                                onChange={(e) =>
                                    setBulkSelectedCostGroup(e.target.value)
                                }
                                isClearable={true}
                                className="bulk-select"
                                size="small"
                            />
                        </div>

                        <div className="bulk-actions">
                            <button
                                className="btn btn-primary btn-sm me-2"
                                disabled={
                                    selectedPositions.size === 0 ||
                                    !bulkSelectedCostGroup
                                }
                                onClick={() =>
                                    handleBulkCostGroupAssignment(
                                        bulkSelectedCostGroup,
                                    )
                                }
                            >
                                {t("lv.cost-group-assignment.apply-cost-group")}
                            </button>
                        </div>
                    </div>

                    {/* Assignment Progress */}
                    <div className="assignment-progress mt-2">
                        <div className="d-flex justify-content-between mb-1">
                            <small>
                                {t("lv.cost-group-assignment.assignment-progress")}:
                                <strong>
                                    {" "}
                                    {costGroupAssignmentProgress.assignedPositions}/
                                    {
                                        costGroupAssignmentProgress.totalPositions
                                    }{" "}
                                </strong>
                                {t("lv.cost-group-assignment.positions-assigned")}
                            </small>
                            <small>
                                <strong>
                                    {costGroupAssignmentProgress.percentage}%
                                </strong>
                            </small>
                        </div>
                        <div className="progress">
                            <div
                                className="progress-bar"
                                role="progressbar"
                                style={{
                                    width: `${costGroupAssignmentProgress.percentage}%`,
                                }}
                                aria-valuenow={
                                    costGroupAssignmentProgress.percentage
                                }
                                aria-valuemin="0"
                                aria-valuemax="100"
                            ></div>
                        </div>
                    </div>
                </div>

                <div className="cost-group-assignment-content">
                    <div className="position-table-container p-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5>
                                {t("lv.cost-group-assignment.positions")}
                            </h5>
                            <div className="table-controls">
                                <DropdownAtom
                                    options={[
                                        {
                                            value: "all",
                                            label: t(
                                                "lv.cost-group-assignment.all",
                                            ),
                                        },
                                        {
                                            value: "assigned",
                                            label: t(
                                                "lv.cost-group-assignment.assigned",
                                            ),
                                        },
                                        {
                                            value: "unassigned",
                                            label: t(
                                                "lv.cost-group-assignment.unassigned",
                                            ),
                                        },
                                    ]}
                                    value={groupFilter}
                                    onChange={(e) =>
                                        setGroupFilter(
                                            e.target.value || e.value,
                                        )
                                    }
                                    className="me-2"
                                    size="small"
                                    placeholder={t(
                                        "lv.cost-group-assignment.filter-groups",
                                    )}
                                />
                                <button
                                    className="btn btn-sm btn-outline-primary me-2"
                                    onClick={() => {
                                        setExpandedGroups(
                                            new Set(allGroupIds),
                                        );
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faExpandAlt}
                                        className="me-1"
                                    />
                                    {t("lv.expand-all")}
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() =>
                                        setExpandedGroups(new Set())
                                    }
                                >
                                    <FontAwesomeIcon
                                        icon={faCompressAlt}
                                        className="me-1"
                                    />
                                    {t("lv.collapse-all")}
                                </button>
                            </div>
                        </div>
                        <div className="position-table-wrapper">
                            <table className="table position-table">
                                <thead className="sticky-header">
                                    <tr>
                                        <th>
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="selectAllPositions"
                                                    onChange={(e) => {
                                                        // Collect all element IDs (both groups and positions) using optional chaining
                                                        const allElementIds =
                                                            [];
                                                        const collectAllElementIds =
                                                            (elements) => {
                                                                if (
                                                                    !elements?.length
                                                                )
                                                                    return;

                                                                elements.forEach(
                                                                    (
                                                                        element,
                                                                    ) => {
                                                                        // Add the current element ID (group or position)
                                                                        allElementIds.push(
                                                                            element.id,
                                                                        );

                                                                        // If it has child elements, recursively collect their IDs
                                                                        if (
                                                                            element
                                                                                ?.elements
                                                                                ?.length
                                                                        ) {
                                                                            collectAllElementIds(
                                                                                element.elements,
                                                                            );
                                                                        }
                                                                    },
                                                                );
                                                            };
                                                        collectAllElementIds(
                                                            hierarchicalPositions,
                                                        );

                                                        // Select or deselect all elements
                                                        if (
                                                            e.target.checked
                                                        ) {
                                                            setSelectedPositions(
                                                                new Set(
                                                                    allElementIds,
                                                                ),
                                                            );
                                                        } else {
                                                            setSelectedPositions(
                                                                new Set(),
                                                            );
                                                        }
                                                    }}
                                                    checked={
                                                        selectedPositions.size >
                                                        0 &&
                                                        getTotalElementCount(
                                                            hierarchicalPositions,
                                                        ) ===
                                                        selectedPositions.size
                                                    }
                                                    title={t(
                                                        "lv.cost-group-assignment.select-all",
                                                    )}
                                                />
                                            </div>
                                        </th>
                                        <th>
                                            {t(
                                                "lv.cost-group-assignment.item-number",
                                            )}
                                        </th>
                                        <th>
                                            {t(
                                                "lv.cost-group-assignment.cost-group",
                                            )}
                                        </th>
                                        <th>
                                            {t("lv.short-text")}
                                        </th>
                                        <th>
                                            {t("lv.quantity")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="scrollable-tbody">
                                    {renderHierarchicalPositions(
                                        filterGroups(hierarchicalPositions),
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LVCostGroupAssignmentPage;
